import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, NavLink as RouterLink, useLocation } from 'react-router-dom';
// material
import { Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
//
import { UserContext } from '../context/userContext';
import Iconify from './Iconify';
import { ROLE } from '../Services/auth.service';

// ----------------------------------------------------------------------

const ListItemStyle = styled((props) => <ListItemButton disableGutters {...props} />)(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  color: theme.palette.text.secondary,
  borderRadius: theme.shape.borderRadius,
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  color: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
};

function NavItem({ item, active }) {
  const theme = useTheme();

  const { i18n } = useTranslation();

  const isActiveRoot = active(item.path);

  const { title, titleEn, path, icon, info, children } = item;

  const [open, setOpen] = useState(isActiveRoot);

  const [lang, setLang] = useState('vi');

  useEffect(() => {
    setLang(i18n.language);
  }, [i18n.language]);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const activeRootStyle = {
    color: 'primary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
  };

  const activeSubStyle = {
    color: 'text.primary',
    fontWeight: 'fontWeightMedium',
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle),
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
          {lang === 'vi' ? (
            <ListItemText disableTypography primary={'vi'} />
          ) : (
            <ListItemText disableTypography primary={'en'} />
          )}
          {info && info}
          <Iconify
            icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((item) => {
              const { title, path } = item;
              const isActiveSub = active(path);

              return (
                <ListItemStyle
                  key={title}
                  component={RouterLink}
                  to={path}
                  sx={{
                    ...(isActiveSub && activeSubStyle),
                  }}
                >
                  <ListItemIconStyle>
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 4,
                        display: 'flex',
                        borderRadius: '50%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'text.disabled',
                        transition: (theme) => theme.transitions.create('transform'),
                        ...(isActiveSub && {
                          transform: 'scale(2)',
                          bgcolor: 'primary.main',
                        }),
                      }}
                    />
                  </ListItemIconStyle>
                  {lang === 'vi' ? (
                    <ListItemText disableTypography primary={title} />
                  ) : (
                    <ListItemText disableTypography primary={titleEn} />
                  )}
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle),
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      {lang === 'vi' ? (
        <ListItemText disableTypography primary={title} />
      ) : (
        <ListItemText disableTypography primary={titleEn} />
      )}
      {info && info}
    </ListItemStyle>
  );
}

NavSection.propTypes = {
  navConfig: PropTypes.array,
};

export default function NavSection({ navConfig, ...other }) {
  const { pathname } = useLocation();
  const { user, isLoading } = useContext(UserContext);

  const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);

  if (isLoading) return <></>;

  return (
    <Box {...other}>
      <List disablePadding sx={{ p: 1 }}>
        {user.role === ROLE.Root
          ? navConfig.map((item) => <NavItem key={item.title} item={item} active={match} />)
          : navConfig.map(
              (item) => item.roles.includes(user?.role) && <NavItem key={item.title} item={item} active={match} />
            )}
        {/* {!user.role === ROLE.Root && */}
        {/* {navConfig.map((item) => (
          <NavItem key={item.title} item={item} active={match} />
        ))} */}
      </List>
    </Box>
  );
}
